import {
  Megaphone, Construction, Droplet, Zap, Trash2, Waves, Lightbulb, TrafficCone,
  AlertTriangle, Camera as CameraIcon, HeartPulse, GraduationCap, ShieldAlert,
  Sparkles, UploadCloud, CheckCircle2, X, Plus, MapPin, Compass
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { saveCurrentLocation } from "../utils/locationUtils";
import axiosInstance from "../utils/axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';

// Import your CustomSelect component
import CustomSelect from "../components/CustomSelect";

// Capacitor Imports
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

// --- ZERO-COST HD THUMBNAIL GENERATOR FOR VIDEOS ---
const generateHDThumbnail = (file) => {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);

    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";

    video.onloadeddata = () => {
      video.currentTime = Math.min(1, video.duration || 0.1);
    };

    video.onseeked = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 640;
      canvas.height = 360;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      resolve(canvas.toDataURL("image/jpeg", 0.7));
      video.src = "";
      video.load();
      URL.revokeObjectURL(url);
    };

    video.onerror = () => {
      video.src = "";
      video.load();
      URL.revokeObjectURL(url);
      resolve(null);
    };
  });
};

export default function ReportIssue() {
  const { t } = useTranslation();
  const routerLocation = useLocation();
  const prefilledData = routerLocation.state?.prefilledData;

  const categories = [
    { label: t('road_&_potholes'), value: "ROAD_&_POTHOLES", icon: Construction },
    { label: t('water_supply'), value: "WATER_SUPPLY", icon: Droplet },
    { label: t('electricity'), value: "ELECTRICITY", icon: Zap },
    { label: t('sanitation'), value: "SANITATION", icon: Trash2 },
    { label: t('garbage'), value: "GARBAGE", icon: Trash2 },
    { label: t('drainage'), value: "DRAINAGE", icon: Waves },
    { label: t('street_lights'), value: "STREET_LIGHTS", icon: Lightbulb },
    { label: t('traffic'), value: "TRAFFIC", icon: TrafficCone },
    { label: t('encroachment'), value: "ENCROACHMENT", icon: AlertTriangle },
    { label: t('health'), value: "HEALTH", icon: HeartPulse },
    { label: t('education'), value: "EDUCATION", icon: GraduationCap },
    { label: t('corruption'), value: "CORRUPTION", icon: ShieldAlert },
    { label: t('other', 'Other'), value: "OTHER", icon: Plus },
  ];

  const user = useSelector((state) => state.auth?.user);

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    location: {
      address: '',
      city: '',
      pinCode: '',
      state: '',
      geoData: { type: 'Point', coordinates: null }
    },
    media: [],
    mediaUrls: [],
    isAnonymous: false
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Upload states
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [previewUrls, setPreviewUrls] = useState([]);
  const [primaryVideoUrl, setPrimaryVideoUrl] = useState(null);
  const uppyRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);

  const [isAILoading, setIsAILoading] = useState(false);
  const [totalResolved, setTotalResolved] = useState(0);

  // --- LOCATION & CSC API STATES ---
  const [isLocating, setIsLocating] = useState(false);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);

  // Default CSC API Config 
  const CSC_HEADERS = { "X-CSCAPI-KEY": import.meta.env.VITE_CSC_API_KEY || "YOUR_CSC_API_KEY_HERE" };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const animationTimer = requestAnimationFrame(() => {
      setIsMounted(true);
    });
    return () => cancelAnimationFrame(animationTimer);
  }, []);

  useEffect(() => {
    if (user) {
      const isAnon = Boolean(user.preferences?.globalAnonymous);
      setFormData(prev => ({ ...prev, isAnonymous: isAnon }));
    }
  }, [user]);

  useEffect(() => {
    const fetchGlobalResolvedCount = async () => {
      try {
        const res = await axiosInstance.get('/issue/global/resolved-count');
        const count = res.data?.resolvedCount || res.data?.count || res.data?.totalResolved;
        if (count !== undefined) setTotalResolved(count);
      } catch (error) { console.warn("Could not fetch global resolved count."); }
    };
    fetchGlobalResolvedCount();
  }, []);

  // FETCH STATES FROM CSC API ON MOUNT
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await fetch("https://api.countrystatecity.in/v1/countries/IN/states", { headers: CSC_HEADERS });
        const data = await res.json();
        if (Array.isArray(data)) setStatesList(data);
      } catch (err) { console.error("CSC API States Error:", err); }
    };
    fetchStates();
  }, []);

  // FETCH CITIES WHEN STATE CHANGES
  useEffect(() => {
    const fetchCities = async () => {
      if (!formData.location.state || statesList.length === 0) return;

      const stateObj = statesList.find(s =>
        s.name.toLowerCase() === formData.location.state.toLowerCase() ||
        s.iso2 === formData.location.state
      );

      if (stateObj) {
        try {
          const res = await fetch(`https://api.countrystatecity.in/v1/countries/IN/states/${stateObj.iso2}/cities`, { headers: CSC_HEADERS });
          const data = await res.json();
          if (Array.isArray(data)) setCitiesList(data);
        } catch (err) { console.error("CSC API Cities Error:", err); }
      }
    };
    fetchCities();
  }, [formData.location.state, statesList]);

  // AUTO LOCATION CAPTURE ENGINE
  const triggerAutoLocation = () => {
    if (isLocating || formData.location.geoData.coordinates) return;

    if (!navigator.geolocation) {
      toast.error(t('gps_not_supported', 'Geolocation is not supported by your browser.'));
      return;
    }

    setIsLocating(true);
    const toastId = toast.loading(t('detecting_location', 'Auto-detecting your location...'));

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        saveCurrentLocation({ latitude, longitude, timestamp: Date.now() });

        setFormData(prev => ({
          ...prev,
          location: { ...prev.location, geoData: { type: 'Point', coordinates: [longitude, latitude] } }
        }));

        try {
          const res = await axiosInstance.post('/get-location-from-coords', { lat: latitude, lng: longitude });
          if (res.data?.success) {
            const { state, city } = res.data.data;
            setFormData(prev => ({
              ...prev,
              location: { ...prev.location, state: state || prev.location.state, city: city || prev.location.city }
            }));
            toast.success(t('location_detected', 'Location securely captured!'), { id: toastId });
          } else {
            throw new Error("Geocode failed");
          }
        } catch (err) {
          console.error("Reverse geocode failed:", err);
          toast.success(t('coords_captured', 'Coordinates captured. Please select your state and city.'), { id: toastId });
        } finally {
          setIsLocating(false);
        }
      },
      (err) => {
        setIsLocating(false);
        toast.error(t('gps_required', 'Please turn on your device GPS/Location to report issues.'), { id: toastId, duration: 4000 });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    if (prefilledData) {
      setFormData(prev => ({
        ...prev,
        title: prefilledData.title || prev.title,
        category: prefilledData.category ? prefilledData.category.toUpperCase() : prev.category,
        description: prefilledData.description || prev.description,
        isAnonymous: prefilledData.isAnonymous !== undefined ? prefilledData.isAnonymous : prev.isAnonymous,
        location: {
          ...prev.location,
          address: prefilledData.location?.address || prev.location.address,
          city: prefilledData.location?.city || prev.location.city,
          state: prefilledData.location?.state || prev.location.state,
          pinCode: prefilledData.location?.pinCode || prev.location.pinCode,
          geoData: {
            type: 'Point',
            coordinates: prefilledData.location?.coordinates || prev.location.geoData.coordinates
          }
        },
        media: prefilledData.originalFiles ? prefilledData.originalFiles : prev.media
      }));

      if (prefilledData.originalFiles && prefilledData.originalFiles.length > 0) {
        try {
          if (prefilledData.originalFiles[0] instanceof Blob) {
            const urls = prefilledData.originalFiles.map(file => URL.createObjectURL(file));
            setPreviewUrls(urls);
          }
        } catch (error) { console.error("Error creating preview URL:", error); }
      }
    }
  }, [prefilledData]);

  useEffect(() => {
    return () => {
      previewUrls.forEach(url => {
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
      if (uppyRef.current) uppyRef.current.destroy();
    };
  }, [previewUrls]);

  useEffect(() => {
    if (formData.media.length > 0 && formData.media[0].type.startsWith('video/')) {
      const url = URL.createObjectURL(formData.media[0]);
      setPrimaryVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPrimaryVideoUrl(null);
    }
  }, [formData.media]);

  const handleFillWithAI = async () => {
    const hasVideo = formData.media.some(file => file.type.startsWith('video/'));

    if (hasVideo) {
      toast.error(t('ai_only_images', 'Only images can be analyzed!'));
      return;
    }

    const imageFiles = formData.media.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      toast.error(t('ai_req_image', 'Please upload at least one image for the AI to analyze.'));
      return;
    }

    setIsAILoading(true);
    setUploadError('');
    setErrors({});

    try {
      const aiFormData = new FormData();
      imageFiles.forEach(file => aiFormData.append('images', file));
      aiFormData.append('city', formData.location.city || user?.contact?.city || '');

      if (formData.location.geoData?.coordinates) {
        aiFormData.append('lng', formData.location.geoData.coordinates[0]);
        aiFormData.append('lat', formData.location.geoData.coordinates[1]);
      }

      aiFormData.append('userHint', formData.description || '');

      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_BASE_URL}/ai/analyze-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: aiFormData
      });

      const data = await response.json();

      if (data?.success && data?.analysis) {
        const aiResult = data.analysis;
        setFormData(prev => ({
          ...prev,
          title: aiResult.title || prev.title,
          // Fallback to "OTHER" if the AI doesn't return a valid category
          category: aiResult.category ? aiResult.category.toUpperCase() : "OTHER",
          description: aiResult.description || prev.description
        }));
        toast.success(t('ai_draft_success', 'Auto-filled successfully!'));
      } else {
        throw new Error(data?.message || t('ai_analysis_fail'));
      }
    } catch (error) {
      console.error("AI Fill Error:", error);
      toast.error(t('ai_fill_fail'));

      // Fallback to "OTHER" if the API call fails completely and no category is currently selected
      setFormData(prev => ({
        ...prev,
        category: prev.category || "OTHER"
      }));
    } finally {
      setIsAILoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'category') triggerAutoLocation();

    if (field.startsWith('location.')) {
      const locationField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        location: { ...prev.location, [locationField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleFileChange = async (e) => {
    triggerAutoLocation();

    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    const totalFilesAfterAdd = formData.media.length + files.length;
    if (totalFilesAfterAdd > 3) {
      setErrors(prev => ({ ...prev, media: `${t('cannot_add')} ${files.length} files. Max is 3.` }));
      return;
    }

    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'video/mp4', 'video/webm', 'video/quicktime'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      setErrors(prev => ({ ...prev, media: t('invalid_media_type', 'Only JPG, PNG, and Videos are allowed.') }));
      return;
    }

    const currentTotalSize = formData.media.reduce((total, file) => total + file.size, 0);
    const newFilesSize = files.reduce((total, file) => total + file.size, 0);
    if (currentTotalSize + newFilesSize > 300 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, media: t('size_limit_exceeded', 'Total size exceeds 300MB limit.') }));
      return;
    }

    setErrors(prev => ({ ...prev, media: '' }));
    setUploadError('');

    const toastId = toast.loading(t('processing_media', 'Processing media...'));
    const newPreviewUrls = [];

    for (const file of files) {
      if (file.type.startsWith('video/')) {
        const thumbBase64 = await generateHDThumbnail(file);
        newPreviewUrls.push(thumbBase64 || 'https://via.placeholder.com/640x360?text=Video+Preview');
      } else {
        newPreviewUrls.push(URL.createObjectURL(file));
      }
    }
    toast.dismiss(toastId);

    setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    setFormData(prev => ({
      ...prev,
      media: [...prev.media, ...files],
      mediaUrls: []
    }));

    e.target.value = '';
  };

  const handleNativeCamera = async () => {
    triggerAutoLocation();
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera
      });

      const response = await fetch(image.webPath);
      const blob = await response.blob();
      const file = new File([blob], `camera_capture_${Date.now()}.${image.format}`, { type: `image/${image.format}` });
      handleFileChange({ target: { files: [file] } });
    } catch (error) {
      console.log('User cancelled or camera error:', error);
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    if (previewUrls[indexToRemove] && previewUrls[indexToRemove].startsWith('blob:')) {
      URL.revokeObjectURL(previewUrls[indexToRemove]);
    }
    const newMedia = formData.media.filter((_, index) => index !== indexToRemove);
    const newPreviewUrls = previewUrls.filter((_, index) => index !== indexToRemove);

    setFormData(prev => ({ ...prev, media: newMedia, mediaUrls: [] }));
    setPreviewUrls(newPreviewUrls);
    setUploadError('');
  };

  const handleCancelUpload = () => {
    if (uppyRef.current) {
      uppyRef.current.cancelAll();
      uppyRef.current.destroy();
      uppyRef.current = null;
    }
    setIsUploading(false);
    setUploadProgress(0);
    setUploadError(t('upload_cancelled', 'Upload was cancelled.'));
  };

  // --- LOCAL UPPY UPLOAD LOGIC ---
  const handleUploadMedia = () => {
    if (formData.media.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError('');

    const uppy = new Uppy({
      autoProceed: true,
      allowMultipleUploadBatches: false,
      retryDelays: [1000, 3000, 5000, 10000, 15000]
    });

    uppy.use(AwsS3, {
      limit: 2,
      getChunkSize: () => 10 * 1024 * 1024,
      timeout: 60 * 1000,
      shouldUseMultipart: true,
      createMultipartUpload: async (file) => {
        const rawName = user?.name || user?.firstName || 'Anonymous';
        const cleanUserName = rawName.replace(/[^a-zA-Z0-9]/g, '_');
        const cleanTitle = (formData.title || 'Issue').replace(/[^a-zA-Z0-9]/g, '_');
        const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
        const uniqueId = Math.random().toString(36).substring(2, 8);
        const extension = file.name.substring(file.name.lastIndexOf('.'));
        const customFileName = `${cleanUserName}_${cleanTitle}_${uniqueId}_${dateStr}${extension}`;

        const res = await axiosInstance.post('/multipart/create', {
          filename: customFileName,
          type: file.type,
          metadata: { category: formData.category || 'Issue', location: formData.location }
        });
        return { uploadId: res.data.uploadId, key: res.data.key };
      },
      signPart: async (file, partData) => {
        const res = await axiosInstance.post('/multipart/sign', {
          uploadId: partData.uploadId, key: partData.key, partNumber: partData.partNumber
        });
        return { url: res.data.url };
      },
      completeMultipartUpload: async (file, uploadData) => {
        const res = await axiosInstance.post('/multipart/complete', {
          uploadId: uploadData.uploadId, key: uploadData.key, parts: uploadData.parts
        });
        return { location: res.data.location };
      },
      abortMultipartUpload: async (file, uploadData) => {
        await axiosInstance.post('/multipart/abort', { uploadId: uploadData.uploadId, key: uploadData.key });
      }
    });

    let successUrls = [];

    uppy.on('progress', (progress) => setUploadProgress(progress));
    uppy.on('upload-success', (file, response) => { successUrls.push(response.body?.location || response.uploadURL); });
    uppy.on('complete', (result) => {
      if (result.failed.length > 0) {
        setUploadError(t('upload_fail_retry', 'Upload failed. Please check your connection and try again.'));
        setIsUploading(false);
        uppy.destroy();
        uppyRef.current = null;
      } else {
        setUploadProgress(100);
        setTimeout(() => {
          setFormData(prev => ({ ...prev, mediaUrls: successUrls }));
          setIsUploading(false);
          toast.success(t('img_up_success', 'Media uploaded to cloud successfully!'));
          uppy.destroy();
          uppyRef.current = null;
        }, 1500);
      }
    });

    formData.media.forEach(file => uppy.addFile({ name: file.name, type: file.type, data: file }));
    uppyRef.current = uppy;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    const validateForm = () => {
      const newErrors = {};

      if (!formData.category) newErrors.category = t('category_req');
      if (!formData.title || !formData.title.trim()) newErrors.title = t('title_req');
      else if (formData.title.trim().split(/\s+/).length > 10) newErrors.title = "Title must be concise (maximum 10 words)";

      if (!formData.description || !formData.description.trim()) newErrors.description = t('desc_req');
      else if (formData.description.trim().split(/\s+/).length < 10) newErrors.description = "At least 10 words required in description";

      // PINCODE IS NO LONGER CHECKED HERE
      if (!formData.location.state || !formData.location.city) {
        setSubmitError(t('req_state_city', 'Please ensure state and city are provided.'));
        setIsSubmitting(false);
        return false;
      }

      if (!formData.location.geoData.coordinates) newErrors.geoData = t('gps_req');

      if (formData.media.length === 0) newErrors.media = "At least 1 media file is required.";
      else if (formData.mediaUrls.length === 0) {
        setUploadError(t('click_upload_before_submit', 'Please click Upload to save your media before submitting.'));
        setIsSubmitting(false);
        return false;
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setIsSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return false;
      }
      return true;
    };

    if (!validateForm()) return;

    const toastId = toast.loading("Submitting issue to the network...");

    try {
      const base64Thumbnails = previewUrls.map(url => url && url.startsWith('data:image') ? url : "");

      // 🟢 THE FIX: Duplicate city into the district field for the backend payload
      let dataToSend = {
        title: formData.title,
        category: formData.category,
        description: formData.description,
        location: {
          ...formData.location,
          district: formData.location.city // Satisfies Mongoose Schema
        },
        media: formData.mediaUrls,
        isAnonymous: formData.isAnonymous,
        thumbnails: base64Thumbnails
      };

      const response = await axiosInstance.post(`/issue`, dataToSend);

      if (response.data) {
        setSubmitSuccess(true);
        setFormData({
          title: '', category: '', description: '',
          location: { address: '', city: '', pinCode: '', state: '', geoData: { type: 'Point', coordinates: null } },
          media: [], mediaUrls: [],
          isAnonymous: Boolean(user?.preferences?.globalAnonymous)
        });

        previewUrls.forEach(url => { if (url && url.startsWith('blob:')) URL.revokeObjectURL(url); });
        setPreviewUrls([]);
        setErrors({});
        setUploadProgress(0);
        localStorage.removeItem('currentLocation');

        setTotalResolved(prev => prev + 1);
        toast.success("Issue successfully reported!", { id: toastId });
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.response?.data?.message || t('something_went_wrong'), { id: toastId });
      setSubmitError(error.response?.data?.message || t('something_went_wrong'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const ghostFilterStyle = isUploading ? {
    filter: `blur(${Math.max(0, 8 - (uploadProgress * 0.08))}px) grayscale(${Math.max(0, 100 - uploadProgress)}%) brightness(${0.5 + (uploadProgress * 0.005)})`
  } : {};

  // Map state and city arrays to the format expected by CustomSelect ({ label, value })
  const stateOptions = statesList.map(state => ({ label: state.name, value: state.name }));
  const cityOptions = citiesList.map(city => ({ label: city.name, value: city.name }));

  return (
    <div
      className={`min-h-[100dvh] bg-texture pb-4 md:pb-6 transition-all duration-500 ease-out ${isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
    >

      {/* HEADER */}
      <header className="glass-card sticky top-2 md:top-4 z-40 mx-2 md:mx-4 rounded-xl md:rounded-2xl shadow-sm border-b border-border/50">
        <div className="flex items-center justify-between px-4 py-3 md:px-8 md:py-6">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="flex h-10 w-10 md:h-12 md:w-12 flex-shrink-0 items-center justify-center rounded-lg md:rounded-xl bg-gradient-to-br from-cyan-600 to-teal-600 shadow-lg">
              <Megaphone className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-gradient leading-tight">Report Issue</h1>
              <p className="text-[10px] md:text-xs text-muted-foreground">{t('community_voice')}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto mt-6 md:mt-8 max-w-7xl px-2 md:px-4">
        <div className="glass-card p-4 md:p-8 shadow-xl rounded-2xl md:rounded-3xl relative">

          {/* =========================================
              CATEGORY & AI AUTO-FILL HEADER
          ========================================== */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-6 md:mb-8 gap-4 border-b border-border/50 pb-4">
            <div>
              <h2 className="mb-1 md:mb-2 text-xl md:text-2xl font-bold text-foreground">{t('select_category_header')}<span className="text-red-600"> *</span></h2>
              <p className="text-xs md:text-sm text-muted-foreground">{t('select_category_desc')}</p>
              {errors.category && <p className="mt-1 text-xs font-semibold text-red-500">{errors.category}</p>}
            </div>

            <button
              onClick={handleFillWithAI}
              disabled={isAILoading || formData.media.length === 0}
              className={`flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-xl font-medium text-sm transition-all shadow-lg ${isAILoading || formData.media.length === 0
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'btn-gradient text-white hover:scale-[1.02] active:scale-[0.98]'
                }`}
            >
              {isAILoading ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>{t('analyzing')}</>
              ) : (
                <><Sparkles className="w-4 h-4" />{t('auto_fill_ai')}</>
              )}
            </button>
          </div>

          {/* CATEGORY GRID */}
          <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {categories.map(({ label, value, icon: Icon }) => (
              <div
                key={value}
                onClick={() => handleInputChange('category', value)}
                className={`group flex cursor-pointer flex-col items-center gap-2 md:gap-3 rounded-xl border-2 border-border p-3 md:p-4 text-xs md:text-sm text-card-foreground transition-all hover:-translate-y-1 hover:border-cyan-600 hover:bg-muted hover:shadow-lg ${formData.category === value ? 'border-cyan-600 bg-muted' : 'bg-muted/20'}`}
              >
                <div className="rounded-lg bg-muted p-2 md:p-3 transition-colors group-hover:bg-cyan-600/10 group-hover:text-cyan-600">
                  <Icon className="h-5 w-5 md:h-6 md:w-6 text-muted-foreground transition-colors group-hover:text-cyan-600" />
                </div>
                <span className="text-center font-medium leading-tight">{label}</span>
              </div>
            ))}
          </div>

          {/* MAIN FORM GRID */}
          <div className="mt-8 md:mt-10 grid gap-6 md:gap-8 lg:grid-cols-3">

            {/* LEFT SIDE: Text Details & Location */}
            <div className="space-y-4 md:space-y-6 lg:col-span-2">
              <div>
                <label className="mb-1.5 md:mb-2 block text-sm font-semibold text-foreground">{t('issue_title')}<span className="text-red-600"> *</span></label>
                <input
                  type="text" placeholder={t('brief_title')} value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full rounded-xl border-2 border-border bg-muted/20 px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base outline-none transition-all focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/20"
                />
                {errors.title && <p className="mt-1 text-xs text-red-600 font-semibold">{errors.title}</p>}
              </div>

              <div>
                <label className="mb-1.5 md:mb-2 block text-sm font-semibold text-foreground">{t('detailed_desc')}<span className="text-red-600"> *</span></label>
                <textarea
                  placeholder={t('issue_desc_placeholder')} value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full rounded-xl border-2 border-border bg-muted/20 px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base outline-none transition-all focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/20 resize-y min-h-[100px]"
                  rows={4}
                />
                {errors.description && <p className="mt-1 text-xs text-red-600 font-semibold">{errors.description}</p>}
              </div>

              {/* LOCATION GRID (State, City, Pin) -> MOVED ABOVE SPECIFIC LOCATION */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-2">
                <div>
                  <label className="mb-1.5 md:mb-2 block text-sm font-semibold text-foreground">
                    {t('state')}<span className="text-red-600"> *</span>
                  </label>
                  <CustomSelect
                    options={stateOptions}
                    value={formData.location.state}
                    onChange={(val) => handleInputChange('location.state', val)}
                    placeholder="Select State"
                    className="bg-muted/20"
                  />
                </div>
                <div>
                  <label className="mb-1.5 md:mb-2 block text-sm font-semibold text-foreground">
                    {t('city')}<span className="text-red-600"> *</span>
                  </label>
                  <CustomSelect
                    options={cityOptions}
                    value={formData.location.city}
                    onChange={(val) => handleInputChange('location.city', val)}
                    placeholder="Select City"
                    disabled={!formData.location.state || citiesList.length === 0}
                    className="bg-muted/20"
                  />
                </div>
                <div>
                  {/* NO ASTERSISK FOR PINCODE */}
                  <label className="mb-1.5 md:mb-2 block text-sm font-semibold text-foreground">{t('pin_code')}</label>
                  <input
                    type="text" placeholder={t('pincode_placeholder')} value={formData.location.pinCode}
                    onChange={(e) => handleInputChange('location.pinCode', e.target.value)}
                    className="w-full rounded-xl border-2 border-border bg-muted/20 px-3 md:px-4 py-2 text-sm md:text-base outline-none transition-all focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/20"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 md:mb-2 block text-sm font-semibold text-foreground">{t('specific_location')}</label>
                <input
                  type="text" placeholder={t('location_placeholder')} value={formData.location.address}
                  onChange={(e) => handleInputChange('location.address', e.target.value)}
                  className="w-full rounded-xl border-2 border-border bg-muted/20 px-3 md:px-4 py-2.5 md:py-3 text-sm md:text-base outline-none transition-all focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/20"
                />
                {errors.location && <p className="mt-1 text-xs text-red-600 font-semibold">{errors.location}</p>}
              </div>

              {/* AUTO GPS STATUS (Removed manual button) */}
              <div className="mt-2 md:mt-4 p-3 md:p-4 rounded-xl bg-muted/20 border border-border">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{t('gps_location')}<span className="text-red-600"> *</span></span>

                    {isLocating ? (
                      <div className="flex items-center gap-1.5 bg-blue-500/10 px-2 py-1 rounded-full">
                        <Compass className="h-3 w-3 text-blue-600 animate-spin" />
                        <span className="text-xs font-medium text-blue-600">Locating...</span>
                      </div>
                    ) : formData.location.geoData.coordinates ? (
                      <div className="flex items-center gap-1.5 bg-green-500/10 px-2 py-1 rounded-full">
                        <div className="h-2.5 w-2.5 rounded-full bg-green-600"></div>
                        <span className="text-xs font-medium text-green-600">{t('captured')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 bg-yellow-500/10 px-2 py-1 rounded-full">
                        <div className="h-2.5 w-2.5 rounded-full bg-yellow-600"></div>
                        <span className="text-xs font-medium text-yellow-600">{t('not_captured')}</span>
                      </div>
                    )}
                  </div>
                </div>
                {errors.geoData && <p className="mt-2 text-xs font-semibold text-red-600">{errors.geoData}</p>}
                {submitError && submitError.includes('state') && <p className="mt-2 text-xs font-semibold text-red-600">{submitError}</p>}
              </div>

            </div>

            {/* RIGHT SIDE: Media & Submit */}
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 md:mb-2 block text-sm font-semibold text-foreground">
                  {t('add_media', 'Add Photos / Videos')}<span className="font-normal text-red-600"> *</span>
                </label>

                <div className={`relative group w-full h-48 md:h-64 rounded-xl overflow-hidden flex items-center justify-center transition-all ${formData.media.length === 0 ? 'border-2 border-dashed border-border bg-muted/20 hover:border-cyan-600' : 'bg-muted/20'}`}>

                  {formData.media.length === 0 ? (
                    <div className="flex w-full h-full bg-muted/20">
                      {Capacitor.isNativePlatform() ? (
                        <button type="button" onClick={handleNativeCamera} className="flex-1 flex flex-col items-center justify-center gap-1 md:gap-2 cursor-pointer hover:bg-muted/50 transition-colors z-10">
                          <CameraIcon className="h-6 w-6 md:h-8 md:w-8 text-muted-foreground group-hover:text-cyan-600 transition-colors" />
                          <span className="text-xs md:text-sm font-medium text-muted-foreground">{t('camera', 'Camera')}</span>
                        </button>
                      ) : (
                        <label className="flex-1 flex flex-col items-center justify-center gap-1 md:gap-2 cursor-pointer hover:bg-muted/50 transition-colors z-10">
                          <CameraIcon className="h-6 w-6 md:h-8 md:w-8 text-muted-foreground group-hover:text-cyan-600 transition-colors" />
                          <span className="text-xs md:text-sm font-medium text-muted-foreground">{t('camera', 'Camera')}</span>
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
                        </label>
                      )}
                      <div className="w-px bg-border my-6"></div>
                      <label className="flex-1 flex flex-col items-center justify-center gap-1 md:gap-2 cursor-pointer hover:bg-muted/50 transition-colors z-10">
                        <UploadCloud className="h-6 w-6 md:h-8 md:w-8 text-muted-foreground group-hover:text-cyan-600 transition-colors" />
                        <span className="text-xs md:text-sm font-medium text-muted-foreground">{t('browse', 'Browse Files')}</span>
                        <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFileChange} />
                      </label>
                      <div className="absolute bottom-2 left-0 right-0 text-center pointer-events-none">
                        <span className="text-[10px] md:text-xs text-muted-foreground px-2">Max 3 files, 300MB total</span>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full relative group cursor-pointer overflow-hidden rounded-xl">
                      {formData.media[0].type.startsWith('video/') ? (
                        <video src={primaryVideoUrl} poster={previewUrls[0]} className="w-full h-full object-cover transition-all duration-300" style={{ ...ghostFilterStyle, backgroundColor: 'black' }} autoPlay loop muted playsInline />
                      ) : (
                        <img src={previewUrls[0]} alt="Primary Preview" className="w-full h-full object-cover transition-all duration-300" style={ghostFilterStyle} />
                      )}

                      {isUploading && (
                        <>
                          <div className="absolute bottom-0 left-0 w-full h-1.5 bg-black/50 z-20">
                            <div className="h-full bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.8)] transition-all duration-300 ease-out" style={{ width: `${uploadProgress}%` }} />
                          </div>
                          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-auto z-20 gap-3">
                            <span className="text-4xl font-black text-white drop-shadow-[0_4px_4px_rgba(0,0,0,0.8)] tracking-tighter">{uploadProgress}%</span>
                            <button type="button" onClick={(e) => { e.stopPropagation(); handleCancelUpload(); }} className="flex items-center gap-1.5 bg-red-600/90 hover:bg-red-700 text-white px-4 py-2 rounded-full text-xs font-bold backdrop-blur-md shadow-lg transition-all transform hover:scale-105 active:scale-95 cursor-pointer z-30">
                              <X className="w-4 h-4" /> Stop Upload
                            </button>
                          </div>
                        </>
                      )}

                      {!isUploading && formData.mediaUrls.length > 0 && (
                        <div className="absolute inset-0 bg-green-500/20 backdrop-blur-[2px] flex flex-col items-center justify-center z-20">
                          <CheckCircle2 className="w-12 h-12 text-green-500 drop-shadow-md mb-2" />
                          <span className="text-white font-bold drop-shadow-md text-sm">Upload Complete</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {errors.media && <p className="mt-2 text-xs font-semibold text-red-600">{errors.media}</p>}
              </div>

              {formData.media.length > 0 && (
                <div className="flex flex-col gap-3 mt-4">
                  {!isUploading && formData.mediaUrls.length === 0 && (
                    <button type="button" onClick={handleUploadMedia} className="w-full btn-gradient py-3 rounded-xl text-white font-bold text-sm shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all flex justify-center items-center gap-2">
                      <UploadCloud className="w-5 h-5" /> Upload {formData.media.length} File{formData.media.length > 1 ? 's' : ''} to Cloud
                    </button>
                  )}

                  {uploadError && (
                    <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-center">
                      <p className="text-xs text-red-500 font-medium">{uploadError}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {formData.media.map((file, index) => (
                      <div key={index} className="relative group aspect-square rounded-lg overflow-hidden bg-muted/20 transition-all">
                        <img src={previewUrls[index]} alt={`Preview ${index + 1}`} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                        {!isUploading && formData.mediaUrls.length === 0 && (
                          <button type="button" onClick={() => handleRemoveFile(index)} className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-1 opacity-100 transition-opacity hover:bg-red-700 shadow-lg z-20 m-1.5">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {file.type.startsWith('video/') && (
                          <div className="absolute bottom-1 left-1 bg-black/60 rounded px-1">
                            <span className="text-[8px] font-bold text-white tracking-widest">VIDEO</span>
                          </div>
                        )}
                      </div>
                    ))}

                    {!isUploading && formData.mediaUrls.length === 0 && formData.media.length > 0 && formData.media.length < 3 && (
                      <label className="relative flex flex-col items-center justify-center aspect-square rounded-lg border-2 border-dashed border-border bg-muted/20 cursor-pointer hover:bg-muted hover:border-cyan-600 transition-all">
                        <Plus className="w-5 h-5 text-muted-foreground mb-0.5" />
                        <span className="text-[9px] font-semibold text-muted-foreground uppercase">{t('add_more', 'Add')}</span>
                        <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFileChange} />
                      </label>
                    )}
                  </div>
                </div>
              )}

              {/* RESTORED ANONYMOUS TOGGLE */}
              <div className="bg-muted/20 p-4 rounded-xl border border-border mt-4">
                <label className="flex items-center gap-3 text-sm font-bold text-foreground cursor-pointer select-none w-full">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={formData.isAnonymous}
                      onChange={(e) => handleInputChange('isAnonymous', e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`block w-10 h-6 rounded-full transition-colors ${formData.isAnonymous ? 'bg-cyan-500' : 'bg-muted border border-border'}`}></div>
                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.isAnonymous ? 'transform translate-x-4' : ''}`}></div>
                  </div>
                  <div className="flex flex-col">
                    <span>Submit Anonymously</span>
                    <span className="text-[10px] text-muted-foreground font-normal mt-0.5">Hide my name from the public feed.</span>
                  </div>
                </label>
              </div>

              <div className="pt-4 border-t border-border/50">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || formData.media.length === 0 || formData.mediaUrls.length === 0 || isUploading}
                  className="btn-gradient w-full rounded-xl py-3 text-sm md:text-base font-semibold text-white shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('submitting') : t('submit_issue')}
                </button>

                {submitError && (
                  <div className="mt-3 rounded-lg bg-red-500/10 border border-red-500/20 p-3">
                    <p className="text-xs font-semibold text-red-600 text-center">{submitError}</p>
                  </div>
                )}

                {submitSuccess && (
                  <div className="mt-3 rounded-lg bg-green-500/10 border border-green-500/20 p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <p className="text-sm text-green-500 font-semibold">{t('issue_submit_success')}</p>
                    </div>
                    <p className="text-xs text-green-600/80 mt-1.5 ml-7">{t('issue_review_shortly')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}